import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/api';
import {
  normalizeContactChannelsData,
  DEFAULT_FREE_DELIVERY_MIN_AMOUNT,
  DEFAULT_STANDARD_SHIPPING_FEE_PKR,
} from '../../utils/whatsappContact';
import './FreeDeliveryPromoSettings.css';

/**
 * Standard shipping fee + free delivery rules (checkout + bag). Revenue → Promo codes tab.
 * Merges with existing /admin/settings/contact so WhatsApp fields are not cleared.
 */
const FreeDeliveryPromoSettings = ({ onNotify }) => {
  const [draft, setDraft] = useState({
    standardShippingFee: DEFAULT_STANDARD_SHIPPING_FEE_PKR,
    freeDeliveryEnabled: true,
    freeDeliveryMinAmount: DEFAULT_FREE_DELIVERY_MIN_AMOUNT,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await settingsService.getContactChannelsAdmin();
        const d = normalizeContactChannelsData(res?.data || res || {});
        if (!cancelled) {
          setDraft({
            standardShippingFee: d.standardShippingFee,
            freeDeliveryEnabled: d.freeDeliveryEnabled,
            freeDeliveryMinAmount: d.freeDeliveryMinAmount,
          });
        }
      } catch (e) {
        if (!cancelled) {
          onNotify?.({
            type: 'error',
            text: e?.message || 'Could not load shipping settings',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onNotify]);

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await settingsService.getContactChannelsAdmin();
      const base = normalizeContactChannelsData(fresh?.data || fresh || {});
      const payload = normalizeContactChannelsData({
        ...base,
        standardShippingFee: draft.standardShippingFee,
        freeDeliveryEnabled: draft.freeDeliveryEnabled,
        freeDeliveryMinAmount: draft.freeDeliveryMinAmount,
      });
      const res = await settingsService.saveContactChannels(payload);
      if (res && res.success === false) {
        throw new Error(res.error || res.message || 'Save failed');
      }
      onNotify?.({ type: 'success', text: 'Shipping & free delivery settings saved.' });
    } catch (e) {
      onNotify?.({ type: 'error', text: e?.message || 'Failed to save shipping settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fd-promo-settings fd-promo-settings--loading">
        <i className="fas fa-spinner fa-spin" aria-hidden />
        <span>Loading shipping settings…</span>
      </div>
    );
  }

  return (
    <div className="fd-promo-settings">
      <div className="fd-promo-settings__header">
        <h3>
          <i className="fas fa-shipping-fast" /> Shipping fees &amp; free delivery
        </h3>
        <button
          type="button"
          className="fd-promo-settings__save"
          onClick={save}
          disabled={saving}
        >
          <i className="fas fa-save" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="fd-promo-settings__body">
        <div className="fd-promo-settings__field">
          <label htmlFor="fd-standard-fee">Standard shipping fee (PKR)</label>
          <input
            id="fd-standard-fee"
            type="number"
            min={0}
            step={50}
            value={draft.standardShippingFee}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              setDraft((d) => ({
                ...d,
                standardShippingFee: Number.isFinite(v) && v >= 0 ? Math.round(v) : DEFAULT_STANDARD_SHIPPING_FEE_PKR,
              }));
            }}
            className="fd-promo-settings__input"
            placeholder={String(DEFAULT_STANDARD_SHIPPING_FEE_PKR)}
          />
          <p className="fd-promo-settings__field-hint">
            Applied when free delivery is off, or when the cart is below the free-delivery minimum.
          </p>
        </div>

        <div className="fd-promo-settings__divider" />

        <p className="fd-promo-settings__hint">
          When free delivery is on, bag and checkout use <strong>PKR 0</strong> shipping once the cart subtotal
          (after sale/coupon) reaches the minimum; otherwise the <strong>standard shipping fee</strong> above applies.
        </p>
        <label className="fd-promo-settings__check">
          <input
            type="checkbox"
            checked={draft.freeDeliveryEnabled}
            onChange={(e) => setDraft((d) => ({ ...d, freeDeliveryEnabled: e.target.checked }))}
          />
          <span>Free delivery on (show on checkout &amp; bag)</span>
        </label>
        <div className="fd-promo-settings__field">
          <label htmlFor="fd-min-amount">Minimum order amount (PKR) for free delivery</label>
          <input
            id="fd-min-amount"
            type="number"
            min={0}
            step={100}
            value={draft.freeDeliveryMinAmount}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              setDraft((d) => ({
                ...d,
                freeDeliveryMinAmount: Number.isFinite(v) ? v : DEFAULT_FREE_DELIVERY_MIN_AMOUNT,
              }));
            }}
            className="fd-promo-settings__input"
            placeholder={String(DEFAULT_FREE_DELIVERY_MIN_AMOUNT)}
          />
        </div>
      </div>
    </div>
  );
};

export default FreeDeliveryPromoSettings;
